/**
 * client/src/pages/Admin.tsx — six tabs, and the two questions each of them answers.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * GENERATIONS IS THE DEFAULT TAB, AND IT TOOK FOURTEEN INVISIBLE FAILURES TO EARN IT.
 *
 * §4.12's *"the analysis produces a dictionary, not a list"* made **TTS** the landing tab, and that
 * was right while the only question an admin had was *which word do people keep respelling*. Then
 * production ran for two days and the real first question turned out to be **what is broken right
 * now** — twelve failed `tts` jobs and two failed `ingest` jobs, days old, with `/api/v1/status`
 * answering 200 and no screen in this product showing any of them.
 *
 * So the order is: **Generations** (what exists and what it cost), **Backlog** (what is stuck and the
 * buttons that unstick it), then TTS's dictionary, then the three operations tabs. `[add to lexicon]`
 * is still the flagship interaction; it is just no longer the first question.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Nothing here is a build flag.** LAW 13 — the screen is reachable only when `isGod(actor)` says
 * so, using the same predicate the server enforces with. A non-god who types the URL gets the same
 * "no page here" a stranger gets, because the routes underneath refuse **404, never 403** and a
 * client that rendered an empty admin shell would confirm the surface exists.
 *
 * ⚠️ **No new CSS.** Every class below already exists in `client/src/ui/app.css` — `.ab-segment` is
 * the tab strip SignIn and Shelf both use, `.ab-table` carries the mobile `data-label` fallback, and
 * `.ab-facts` is Book.tsx's definition list. AGENTS.md: never hardcode a hex, and the way not to is
 * to not write a stylesheet.
 *
 * ⚠️ **The Health tab fetches TWO endpoints on purpose.** `GET /api/v1/status` is LAW 21's dependency
 * report and is where origin latency, disk percent, queue depth and backup ages are actually measured;
 * `GET /api/v1/admin/health` adds only the god-only counts. Rendering one from the other would be a
 * second implementation of "is this box healthy" — see `routes/admin.ts`'s header.
 */

import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { isGod } from "../../../shared/access.ts";
import type { PatternFeatures } from "../../../shared/narration/hotspots.ts";
import { Backlog, type BacklogResponse } from "../admin/Backlog.tsx";
import { Generations, type GenerationsResponse } from "../admin/Generations.tsx";
import { Readiness, type TtsStatus } from "../admin/Readiness.tsx";
import { Voices, type VoicesResponse } from "../admin/Voices.tsx";
import { AppFrame, Blank, timeAgo } from "../App.tsx";
import { ApiError, api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";

/* ── the wire ─────────────────────────────────────────────────────────────── */

interface KnownEntry {
  id: string;
  scope: string;
  bookId: string | null;
  replacement: string;
  hits: number;
}

interface PairRow {
  from: string;
  to: string;
  regens: number;
  books: number;
  users: number;
  appliesToPrinted: boolean;
  resolved: boolean;
  sample: string;
  entry: KnownEntry | null;
}

interface SentenceRow {
  anchor: string;
  regens: number;
  reports: number;
  books: number;
  users: number;
  reasons: string[];
  sample: string;
}

interface PatternRow {
  key: string;
  features: PatternFeatures;
  regens: number;
  books: number;
  reasons: string[];
  sample: string;
}

interface Hotspots {
  window: { since: number | null; days: number | null; events: number; truncated: boolean };
  pairs: PairRow[];
  sentences: SentenceRow[];
  patterns: PatternRow[];
  lexicon: { entries: number };
}

interface PromoteResult {
  applied: boolean;
  summary: string;
  resolved: number;
  impact: { books: number; sentences: number; shadowed: number; pagesNarrated: number; estGpuSec: number };
}

interface AdminBook {
  id: string;
  title: string;
  author: string | null;
  visibility: string;
  publicRequestedAt: number | null;
  takenDownAt: number | null;
  takedownReason: string | null;
  createdAt: number;
  owner: { handle: string | null; email: string | null };
}

interface AdminHealth {
  library: { books: number; users: number; editions: number; pendingPublic: number; takenDown: number };
  audio: { clips: number; pageArtifacts: number };
  lexicon: { entries: number };
  telemetry: {
    regenEvents: number;
    regenApplied: number;
    unsettled: number;
    listenRaw: number;
    listenDaily: number;
    oldestRawAt: number | null;
    buffer: { pending: number; dropped: number };
  };
}

interface StatusReport {
  disk?: { percent?: number; freeBytes?: number } | null;
  queue?: { lane: string; status: string; n: number }[];
  origin?: { reachable?: boolean; latencyMs?: number | null } | null;
  layout?: { p95Ms?: number | null; unstable?: number; alarm?: boolean } | null;
  /**
   * ⚠️ This used to be typed `{ mode?: string; hosts?: string[] }` and the `hosts: string[]` half was
   * a **config echo** — the contents of `AB_TTS_HOSTS`, restated, with no reachability in it. It is
   * now `sys/readiness.ts`'s two-layer report and `hosts` is `HostReport[]`. Typed by importing the
   * shape the readiness screen already declares rather than restating it, because two hand-written
   * copies of one wire format is how the admin page and the status page come to disagree during the
   * one outage anybody reads them both in.
   */
  tts?: TtsStatus | null;
  warnings?: string[];
}

interface AdminUserSummary {
  id: string;
  handle: string;
  displayName: string;
  email: string;
  isAdmin: boolean;
  createdAt: number;
  lastSeenAt: number | null;
  deletedAt: number | null;
  metrics: {
    listenedMs: number;
    booksPlayed: number;
    chaptersPlayed: number;
    booksRead: number;
    chaptersRead: number;
    booksOwned: number;
    booksAccessible: number;
    explicitBooks: number;
  };
  recent: {
    bookId: string;
    title: string;
    chapterOrd: number | null;
    chapterTitle: string | null;
    kind: "read" | "listen";
    at: number;
  } | null;
}

interface AdminUsersResponse {
  users: AdminUserSummary[];
  metricsNote: string;
}

interface AdminUserDetail {
  user: AdminUserSummary;
  activity: Array<{
    bookId: string;
    title: string;
    listenedMs: number;
    lastHeardAt: number | null;
    lastReadAt: number | null;
    lastPageId: string | null;
    lastSid: string | null;
    chapterOrd: number | null;
    chapterTitle: string | null;
    updatedAt: number;
  }>;
  access: Array<{
    book: {
      id: string;
      title: string;
      author: string | null;
      visibility: string;
      takenDownAt: number | null;
      owner: { id: string; handle: string } | null;
    };
    effective: boolean;
    basis: string[];
    explicit: {
      active: boolean;
      grantedAt: number;
      grantedBy: string;
      revokedAt: number | null;
      revokedBy: string | null;
    } | null;
    canGrant: boolean;
    canRevoke: boolean;
  }>;
}

/* ── a tiny fetch hook, local on purpose ──────────────────────────────────── */

/**
 * `useCachedQuery` is not used here and that is deliberate: it caches into IndexedDB, and an admin
 * screen that renders yesterday's moderation queue from a local cache is one where a god approves a
 * book twice and takes down one that was already lifted.
 */
function useAsk<T>(path: string | null, nonce: number): { data: T | null; error: string | null; loading: boolean } {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: path !== null,
  });

  useEffect(() => {
    if (path === null) return;
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    api
      .get<T>(path)
      .then((data) => {
        if (live) setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (live) setState({ data: null, error: err instanceof ApiError ? err.message : String(err), loading: false });
      });
    return () => {
      live = false;
    };
  }, [path, nonce]);

  return state;
}

/* ── the screen ───────────────────────────────────────────────────────────── */

type Tab = "gen" | "backlog" | "users" | "tts" | "voices" | "books" | "rooms" | "health";

const TABS: readonly { id: Tab; label: string }[] = [
  { id: "gen", label: "Generations" },
  { id: "backlog", label: "Backlog" },
  { id: "users", label: "Users" },
  { id: "tts", label: "TTS" },
  { id: "voices", label: "Voices" },
  { id: "books", label: "Books" },
  { id: "rooms", label: "Rooms" },
  { id: "health", label: "Health" },
];

export function Admin(): ReactElement {
  const { actor, loading } = useAuth();
  // `gen` and not `tts`: the first question an admin has is "what is broken", and for two days the
  // answer was fourteen failed jobs that no screen in this product showed. See the header.
  const [tab, setTab] = useState<Tab>("gen");

  if (loading) return <Blank />;
  // The same refusal a stranger gets. Not a "you are not an admin" page: that is a 403 rendered in
  // HTML, and it confirms the surface exists to anybody who guesses the URL.
  if (!isGod(actor)) return <Navigate to="/" replace />;

  return (
    <AppFrame wide>
      <h1 className="ab-h1">Admin</h1>
      <p className="ab-lede">
        Everything on this screen is about the whole server, not about one book.
      </p>

      <div className="ab-segment" role="tablist" aria-label="Admin sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "ab-segment-item is-on" : "ab-segment-item"}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "gen" ? <GenerationsTab /> : null}
      {tab === "backlog" ? <BacklogTab /> : null}
      {tab === "users" ? <UsersTab /> : null}
      {tab === "tts" ? <TtsTab /> : null}
      {tab === "voices" ? <VoicesTab /> : null}
      {tab === "books" ? <BooksTab /> : null}
      {tab === "rooms" ? <RoomsTab /> : null}
      {tab === "health" ? <HealthTab /> : null}
    </AppFrame>
  );
}

/* ── Generations ──────────────────────────────────────────────────────────── */

/**
 * The fetching half. `Generations.tsx` is the rendering half and takes its data as props, so the
 * table can be exercised without a network — the two halves are split at the same seam every other
 * screen here uses.
 */
function GenerationsTab(): ReactElement {
  const [nonce, setNonce] = useState(0);
  const got = useAsk<GenerationsResponse>("/admin/generations", nonce);
  return (
    <Generations
      data={got.data}
      error={got.error}
      loading={got.loading}
      onRefresh={() => setNonce((n) => n + 1)}
    />
  );
}

/* ── Backlog ──────────────────────────────────────────────────────────────── */

function BacklogTab(): ReactElement {
  const [nonce, setNonce] = useState(0);
  // `failed` first, deliberately. This tab exists because fourteen failed jobs were invisible; opening
  // it on `queued` would put the busiest list in front of the one that needs a human.
  const [status, setStatus] = useState("failed");
  const [lane, setLane] = useState("");

  const path = `/admin/jobs?status=${encodeURIComponent(status)}${lane === "" ? "" : `&lane=${encodeURIComponent(lane)}`}`;
  const got = useAsk<BacklogResponse>(path, nonce);

  return (
    <Backlog
      data={got.data}
      error={got.error}
      loading={got.loading}
      lane={lane}
      status={status}
      onFilter={(next) => {
        if (next.lane !== undefined) setLane(next.lane);
        if (next.status !== undefined) setStatus(next.status);
      }}
      onRefresh={() => setNonce((n) => n + 1)}
    />
  );
}

/* ── Users ───────────────────────────────────────────────────────────────── */

function UsersTab(): ReactElement {
  const [nonce, setNonce] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [bookQuery, setBookQuery] = useState("");
  const [accessibleOnly, setAccessibleOnly] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const users = useAsk<AdminUsersResponse>("/admin/users", nonce);
  const detail = useAsk<AdminUserDetail>(selected === null ? null : `/admin/users/${encodeURIComponent(selected)}`, nonce);

  const shownUsers = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === "") return users.data?.users ?? [];
    return (users.data?.users ?? []).filter((user) =>
      `${user.displayName} ${user.handle} ${user.email}`.toLocaleLowerCase().includes(needle),
    );
  }, [query, users.data]);

  const shownAccess = useMemo(() => {
    const needle = bookQuery.trim().toLocaleLowerCase();
    return (detail.data?.access ?? []).filter((row) => {
      if (accessibleOnly && !row.effective) return false;
      return needle === "" || `${row.book.title} ${row.book.author ?? ""}`.toLocaleLowerCase().includes(needle);
    });
  }, [accessibleOnly, bookQuery, detail.data]);

  const changeAccess = useCallback(
    async (bookId: string, grant: boolean) => {
      if (selected === null) return;
      if (!grant && !window.confirm("Revoke this named access? Their next book request will stop, and an active room may eject them.")) {
        return;
      }
      setBusy(bookId);
      setActionError(null);
      try {
        const path = `/admin/users/${encodeURIComponent(selected)}/books/${encodeURIComponent(bookId)}`;
        if (grant) await api.put(path, {});
        else await api.del(path);
        setNonce((n) => n + 1);
      } catch (err) {
        setActionError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [selected],
  );

  if (users.loading && users.data === null) return <Panel>Reading account activity…</Panel>;
  if (users.error !== null) return <Panel bad>{users.error}</Panel>;

  return (
    <>
      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <div>
            <h2 className="ab-h2">Accounts</h2>
            <p className="ab-hint">
              Last seen includes opening the app, saved reading positions, and live audio requests. {users.data?.metricsNote}
            </p>
          </div>
          <label className="ab-search">
            <span className="ab-sr">Find a user</span>
            <input
              type="search"
              value={query}
              placeholder="Name, handle or email"
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>

        {shownUsers.length === 0 ? (
          <p className="ab-hint">No account matches that search.</p>
        ) : (
          <table className="ab-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Last seen</th>
                <th scope="col">Listening</th>
                <th scope="col">Reading</th>
                <th scope="col">Current / recent</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {shownUsers.map((user) => (
                <tr key={user.id}>
                  <td data-label="Person">
                    {user.displayName} <span className="ab-hint-quiet">@{user.handle}</span>
                    {user.isAdmin ? <> <span className="ab-chip">god</span></> : null}
                    {user.deletedAt !== null ? <> <span className="ab-chip ab-chip-bad">deleted</span></> : null}
                    <span className="ab-table-detail">{user.email} · joined {timeAgo(user.createdAt)}</span>
                  </td>
                  <td data-label="Last seen">{user.lastSeenAt === null ? "Not observed yet" : timeAgo(user.lastSeenAt)}</td>
                  <td data-label="Listening" className="ab-num">
                    {durationLabel(user.metrics.listenedMs)} · {user.metrics.booksPlayed} book{user.metrics.booksPlayed === 1 ? "" : "s"} · {user.metrics.chaptersPlayed} chapter{user.metrics.chaptersPlayed === 1 ? "" : "s"}
                  </td>
                  <td data-label="Reading" className="ab-num">
                    {user.metrics.booksRead} book{user.metrics.booksRead === 1 ? "" : "s"} · {user.metrics.chaptersRead} chapter{user.metrics.chaptersRead === 1 ? "" : "s"}
                  </td>
                  <td data-label="Current / recent" className="ab-table-detail">
                    {user.recent === null ? (
                      "No book activity yet"
                    ) : (
                      <>
                        {user.recent.kind === "listen" ? "Played" : "Read"} <strong>{user.recent.title}</strong>
                        {user.recent.chapterTitle ? ` · ${user.recent.chapterTitle}` : user.recent.chapterOrd === null ? "" : ` · chapter ${user.recent.chapterOrd + 1}`}
                        {` · ${timeAgo(user.recent.at)}`}
                      </>
                    )}
                  </td>
                  <td data-label="">
                    <button type="button" className="ab-btn ab-btn-sm" onClick={() => setSelected(user.id)}>
                      {selected === user.id ? "Selected" : "Inspect"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected !== null ? (
        detail.loading && detail.data === null ? (
          <Panel>Reading that account…</Panel>
        ) : detail.error !== null ? (
          <Panel bad>{detail.error}</Panel>
        ) : detail.data !== null ? (
          <UserDetailPanel
            detail={detail.data}
            shownAccess={shownAccess}
            bookQuery={bookQuery}
            accessibleOnly={accessibleOnly}
            busy={busy}
            error={actionError}
            onBookQuery={setBookQuery}
            onAccessibleOnly={setAccessibleOnly}
            onChangeAccess={changeAccess}
          />
        ) : null
      ) : (
        <Panel>Select an account to see its recent books and exactly why each book is available.</Panel>
      )}
    </>
  );
}

function UserDetailPanel({
  detail,
  shownAccess,
  bookQuery,
  accessibleOnly,
  busy,
  error,
  onBookQuery,
  onAccessibleOnly,
  onChangeAccess,
}: {
  detail: AdminUserDetail;
  shownAccess: AdminUserDetail["access"];
  bookQuery: string;
  accessibleOnly: boolean;
  busy: string | null;
  error: string | null;
  onBookQuery: (value: string) => void;
  onAccessibleOnly: (value: boolean) => void;
  onChangeAccess: (bookId: string, grant: boolean) => Promise<void>;
}): ReactElement {
  const user = detail.user;
  return (
    <>
      <section className="ab-panel">
        <h2 className="ab-h2">{user.displayName} · @{user.handle}</h2>
        <dl className="ab-facts">
          <div><dt>Estimated listening</dt><dd>{durationLabel(user.metrics.listenedMs)}</dd></div>
          <div><dt>Books played</dt><dd>{user.metrics.booksPlayed}</dd></div>
          <div><dt>Chapters played</dt><dd>{user.metrics.chaptersPlayed}</dd></div>
          <div><dt>Books read</dt><dd>{user.metrics.booksRead}</dd></div>
          <div><dt>Chapters read</dt><dd>{user.metrics.chaptersRead}</dd></div>
          <div><dt>Owns</dt><dd>{user.metrics.booksOwned}</dd></div>
          <div><dt>Can read</dt><dd>{user.metrics.booksAccessible}</dd></div>
          <div><dt>Named grants</dt><dd>{user.metrics.explicitBooks}</dd></div>
        </dl>
        <p className="ab-hint-quiet">
          Played/read counts are unique books and chapters the server observed, not completion claims. Existing activity from before this dashboard has no invented duration.
        </p>
      </section>

      <section className="ab-panel">
        <h2 className="ab-h2">Recent books</h2>
        {detail.activity.length === 0 ? (
          <p className="ab-hint">No measured book activity yet.</p>
        ) : (
          <table className="ab-table">
            <thead><tr><th scope="col">Book</th><th scope="col">Last activity</th><th scope="col">Listening</th><th scope="col">Place</th></tr></thead>
            <tbody>
              {detail.activity.map((row) => (
                <tr key={row.bookId}>
                  <td data-label="Book">{row.title}</td>
                  <td data-label="Last activity">{timeAgo(row.updatedAt)}</td>
                  <td data-label="Listening" className="ab-num">{durationLabel(row.listenedMs)}</td>
                  <td data-label="Place" className="ab-table-detail">
                    {row.chapterTitle ?? (row.chapterOrd === null ? "—" : `Chapter ${row.chapterOrd + 1}`)}
                    {row.lastPageId ? ` · ${row.lastPageId}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <div>
            <h2 className="ab-h2">Book access</h2>
            <p className="ab-hint">
              Ownership, public visibility, unlisted-by-ID access, and godmode are derived and cannot be revoked here. A named grant adds reading, listening, offline caching, and rooms to a private book; it never adds editing, regeneration, or file export.
            </p>
          </div>
          <div className="ab-segment" role="group" aria-label="Which access rows">
            <button type="button" className={accessibleOnly ? "ab-segment-item is-on" : "ab-segment-item"} onClick={() => onAccessibleOnly(true)}>Available</button>
            <button type="button" className={!accessibleOnly ? "ab-segment-item is-on" : "ab-segment-item"} onClick={() => onAccessibleOnly(false)}>Every book</button>
          </div>
          <label className="ab-search">
            <span className="ab-sr">Find a book</span>
            <input type="search" value={bookQuery} placeholder="Title or author" onChange={(event) => onBookQuery(event.target.value)} />
          </label>
        </div>
        {error !== null ? <p className="ab-hint-bad">{error}</p> : null}
        {shownAccess.length === 0 ? (
          <p className="ab-hint">No books match this view.</p>
        ) : (
          <table className="ab-table">
            <thead><tr><th scope="col">Book</th><th scope="col">Why</th><th scope="col">Owner</th><th scope="col" /></tr></thead>
            <tbody>
              {shownAccess.map((row) => (
                <tr key={row.book.id}>
                  <td data-label="Book">
                    {row.book.title}{row.book.author ? <span className="ab-hint-quiet"> · {row.book.author}</span> : null}
                    {row.book.takenDownAt !== null ? <> <span className="ab-chip ab-chip-bad">taken down</span></> : null}
                  </td>
                  <td data-label="Why">
                    {row.effective ? row.basis.map((basis) => <span key={basis} className="ab-chip">{basisLabel(basis)}</span>) : <span className="ab-hint-quiet">No access</span>}
                    {row.explicit !== null && !row.explicit.active ? <span className="ab-hint-quiet"> named grant revoked {row.explicit.revokedAt === null ? "" : timeAgo(row.explicit.revokedAt)}</span> : null}
                  </td>
                  <td data-label="Owner">{row.book.owner?.handle ?? "—"}</td>
                  <td data-label="">
                    {row.canRevoke ? (
                      <button type="button" className="ab-btn ab-btn-danger ab-btn-sm" disabled={busy === row.book.id} onClick={() => void onChangeAccess(row.book.id, false)}>
                        {busy === row.book.id ? "Saving…" : "Revoke named access"}
                      </button>
                    ) : row.canGrant ? (
                      <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" disabled={busy === row.book.id} onClick={() => void onChangeAccess(row.book.id, true)}>
                        {busy === row.book.id ? "Saving…" : "Grant access"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function durationLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0 min";
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

function basisLabel(basis: string): string {
  if (basis === "unlisted-by-id") return "unlisted · URL/ID";
  if (basis === "explicit") return "named grant";
  return basis;
}

/* ── Voices ───────────────────────────────────────────────────────────────── */

/**
 * P7c. Separate from the TTS tab on purpose: that one answers *"can the box make audio right now"*
 * (readiness, and the dictionary of what people keep respelling), this one answers *"which voices do
 * we have, and can a book actually use them"*. They were one tab for about ten minutes and the table
 * that matters — a voice on the box that `AB_TTS_HOSTS` maps to nothing — was below the fold.
 */
function VoicesTab(): ReactElement {
  const [nonce, setNonce] = useState(0);
  const got = useAsk<VoicesResponse>("/admin/voices", nonce);
  return <Voices data={got.data} error={got.error} loading={got.loading} onRefresh={() => setNonce((n) => n + 1)} />;
}

/* ── TTS ──────────────────────────────────────────────────────────────────── */

function TtsTab(): ReactElement {
  const [days, setDays] = useState("90");
  const [nonce, setNonce] = useState(0);
  const hot = useAsk<Hotspots>(`/admin/tts/hotspots?days=${encodeURIComponent(days)}`, nonce);
  /**
   * ⚠️ **Readiness is fetched separately and rendered ABOVE the dictionary, and both halves of that
   * are deliberate.**
   *
   * Separately, because a `/health` outage must not blank the hotspot table and a slow hotspot query
   * must not delay the one screen that answers *"can the box make audio at all"*. Above, because
   * §4.12's dictionary is the right first question only while the machine works — on 2026-08-08
   * `variant: "full"` was a hard HTTP 500 on every voice and the tab it belongs on opened with a
   * ranking of misspelled words.
   */
  const ready = useAsk<TtsStatus>("/admin/tts/ready", nonce);
  const [view, setView] = useState<"pairs" | "sentences" | "patterns">("pairs");

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  if (hot.loading && hot.data === null) return <Panel>Reading the regeneration log…</Panel>;
  if (hot.error !== null) return <Panel bad>{hot.error}</Panel>;
  const data = hot.data;
  if (data === null) return <Panel bad>No answer.</Panel>;

  return (
    <>
      {ready.error !== null ? <Panel bad>{ready.error}</Panel> : <Readiness tts={ready.data} onChanged={refetch} />}

      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <div className="ab-segment" role="group" aria-label="Which ranking">
            {(
              [
                { id: "pairs", label: `Words (${data.pairs.length})` },
                { id: "sentences", label: `Sentences (${data.sentences.length})` },
                { id: "patterns", label: `Shapes (${data.patterns.length})` },
              ] as const
            ).map((v) => (
              <button
                key={v.id}
                type="button"
                className={view === v.id ? "ab-segment-item is-on" : "ab-segment-item"}
                onClick={() => setView(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="ab-select">
            <label className="ab-sr" htmlFor="ab-admin-window">
              How far back
            </label>
            <select id="ab-admin-window" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">Everything</option>
            </select>
          </div>
        </div>
        <p className="ab-hint">
          {data.window.events} regeneration{data.window.events === 1 ? "" : "s"} and report
          {data.window.events === 1 ? "" : "s"} in this window · {data.lexicon.entries} lexicon entr
          {data.lexicon.entries === 1 ? "y" : "ies"}
          {data.window.truncated ? " · truncated at 20 000 events — narrow the window" : ""}
        </p>
      </section>

      {view === "pairs" ? <PairsTable rows={data.pairs} onChanged={refetch} /> : null}
      {view === "sentences" ? <SentencesTable rows={data.sentences} /> : null}
      {view === "patterns" ? <PatternsTable rows={data.patterns} /> : null}
    </>
  );
}

function PairsTable({ rows, onChanged }: { rows: PairRow[]; onChanged: () => void }): ReactElement {
  if (rows.length === 0) {
    return (
      <Panel>
        <strong>Nothing to promote yet.</strong>
        <p className="ab-hint">
          This table fills up when somebody uses <em>Fix pronunciation…</em> in the reader. Each row is a
          word one or more people respelled by hand; promoting it spells that word correctly in every
          book on this server, including the ones nobody has uploaded yet.
        </p>
      </Panel>
    );
  }

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">What people are respelling</h2>
      <p className="ab-hint">
        Ranked by <strong>how many books</strong> hit it, then by how many people — one person
        regenerating the same sentence fourteen times is a bad afternoon, not a dictionary entry.
      </p>
      <table className="ab-table">
        <thead>
          <tr>
            <th scope="col">As printed → as spoken</th>
            <th scope="col">Seen</th>
            <th scope="col">Sample</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <PairRowView key={`${r.from}\u0000${r.to}`} row={r} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PairRowView({ row, onChanged }: { row: PairRow; onChanged: () => void }): ReactElement {
  const [preview, setPreview] = useState<PromoteResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<PromoteResult | null>(null);

  const call = useCallback(
    async (apply: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const res = await api.post<PromoteResult>("/admin/tts/lexicon", { from: row.from, to: row.to, scope: "global", apply });
        if (apply) {
          setDone(res);
          onChanged();
        } else {
          setPreview(res);
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [row.from, row.to, onChanged],
  );

  return (
    <tr>
      <td data-label="Pair">
        <code className="ab-sid">{row.from}</code> → <code className="ab-sid">{row.to}</code>
        {row.entry !== null ? (
          <>
            {" "}
            <span className="ab-chip ab-chip-quiet">already in the lexicon</span>
          </>
        ) : null}
        {!row.appliesToPrinted ? (
          <>
            {" "}
            {/* Not cosmetic. `speakable()` applies the overrides bag at stage 1, against the PRINTED
                sentence — so a pair whose left side exists only after normalisation names a string the
                normaliser will never see, and the rule would match nothing, forever, silently. */}
            <span className="ab-chip ab-chip-warn">not in the printed text</span>
          </>
        ) : null}
      </td>
      <td data-label="Seen" className="ab-num">
        {row.regens} regen{row.regens === 1 ? "" : "s"} · {row.books} book{row.books === 1 ? "" : "s"} ·{" "}
        {row.users} user{row.users === 1 ? "" : "s"}
      </td>
      <td className="ab-table-detail" data-label="Sample">
        {row.sample === "" ? <span className="ab-hint-quiet">—</span> : row.sample}
      </td>
      <td data-label="">
        {done !== null ? (
          <span className="ab-hint">
            Added. {done.summary}
            {done.resolved > 0 ? ` · ${done.resolved} report${done.resolved === 1 ? "" : "s"} closed` : ""}
          </span>
        ) : preview !== null ? (
          <span className="ab-actions">
            <span className="ab-hint">{preview.summary}</span>
            <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" disabled={busy} onClick={() => void call(true)}>
              {busy ? "Adding…" : "Add it"}
            </button>
            <button type="button" className="ab-btn ab-btn-sm" onClick={() => setPreview(null)}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="ab-btn ab-btn-sm"
            disabled={busy || row.entry !== null || !row.appliesToPrinted}
            onClick={() => void call(false)}
          >
            {busy ? "Counting…" : "add to lexicon"}
          </button>
        )}
        {error !== null ? <p className="ab-hint-bad">{error}</p> : null}
      </td>
    </tr>
  );
}

function SentencesTable({ rows }: { rows: SentenceRow[] }): ReactElement {
  if (rows.length === 0) return <Panel>Nobody has reported a sentence yet.</Panel>;
  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Sentences people flagged</h2>
      <p className="ab-hint">
        The weakest of the three rankings, and it is here because it is the one that says where to go
        and listen. <strong>Reports</strong> are “Sounds wrong” — zero GPU, and the only signal an
        anonymous reader has.
      </p>
      <table className="ab-table">
        <thead>
          <tr>
            <th scope="col">Sentence</th>
            <th scope="col">Counts</th>
            <th scope="col">Why</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.anchor}>
              <td className="ab-table-detail" data-label="Sentence">
                {r.sample === "" ? <code className="ab-sid">{r.anchor.slice(0, 12)}</code> : r.sample}
              </td>
              <td data-label="Counts" className="ab-num">
                {r.regens} regen{r.regens === 1 ? "" : "s"} · {r.reports} report{r.reports === 1 ? "" : "s"} ·{" "}
                {r.books} book{r.books === 1 ? "" : "s"}
              </td>
              <td data-label="Why">{r.reasons.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PatternsTable({ rows }: { rows: PatternRow[] }): ReactElement {
  if (rows.length === 0) {
    return (
      <Panel>
        Nothing here yet. This table only fills with regenerations that <em>changed no text</em> — where
        the spelling was already right and the reading was still wrong.
      </Panel>
    );
  }
  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Shapes the model reads badly</h2>
      <p className="ab-hint">
        Regenerations that changed <strong>no text</strong>, clustered by the shape of the sentence
        rather than by its words. No lexicon entry fixes any of these — what they are worth is knowing
        which shapes to send to the slower <code>full</code> variant automatically.
      </p>
      <table className="ab-table">
        <thead>
          <tr>
            <th scope="col">Shape</th>
            <th scope="col">Seen</th>
            <th scope="col">Sample</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td data-label="Shape">
                <code className="ab-sid">{describeShape(r.features)}</code>
              </td>
              <td data-label="Seen" className="ab-num">
                {r.regens} · {r.books} book{r.books === 1 ? "" : "s"}
              </td>
              <td className="ab-table-detail" data-label="Sample">
                {r.sample}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** The six features as English, in the order §4.12 lists them. */
function describeShape(f: PatternFeatures): string {
  const bits = [f.length, f.terminal === "none" ? "no terminal" : `ends “${f.terminal}”`];
  if (f.digits) bits.push("digits");
  if (f.emDash) bits.push("em dash");
  if (f.quoted) bits.push("quoted");
  if (f.allCaps) bits.push("ALL CAPS");
  return bits.join(" · ");
}

/* ── Books ────────────────────────────────────────────────────────────────── */

function BooksTab(): ReactElement {
  const [pendingOnly, setPendingOnly] = useState(true);
  const [nonce, setNonce] = useState(0);
  const got = useAsk<{ books: AdminBook[] }>(`/admin/books?pending=${pendingOnly ? "1" : "0"}`, nonce);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = useCallback(async (id: string, what: "approve" | "takedown" | "lift") => {
    setBusy(id);
    setError(null);
    try {
      if (what === "approve") await api.post(`/admin/books/${encodeURIComponent(id)}/approve-public`, {});
      else if (what === "lift") await api.post(`/admin/books/${encodeURIComponent(id)}/takedown`, { lift: true });
      else {
        const reason = window.prompt("Why is this coming down? The owner is shown this.");
        if (reason === null || reason.trim() === "") return;
        await api.post(`/admin/books/${encodeURIComponent(id)}/takedown`, { reason });
      }
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }, []);

  const books = got.data?.books ?? [];

  return (
    <section className="ab-panel">
      <div className="ab-shelf-controls">
        <div className="ab-segment" role="group" aria-label="Which books">
          <button type="button" className={pendingOnly ? "ab-segment-item is-on" : "ab-segment-item"} onClick={() => setPendingOnly(true)}>
            Waiting for approval
          </button>
          <button type="button" className={!pendingOnly ? "ab-segment-item is-on" : "ab-segment-item"} onClick={() => setPendingOnly(false)}>
            Every book
          </button>
        </div>
      </div>

      {got.error !== null ? <p className="ab-hint-bad">{got.error}</p> : null}
      {error !== null ? <p className="ab-hint-bad">{error}</p> : null}

      {books.length === 0 ? (
        <p className="ab-hint">
          {pendingOnly ? "Nobody is waiting. A book only lands here when its owner asks for it to be public." : "No books."}
        </p>
      ) : (
        <table className="ab-table">
          <thead>
            <tr>
              <th scope="col">Book</th>
              <th scope="col">Owner</th>
              <th scope="col">State</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {books.map((b) => (
              <tr key={b.id}>
                <td data-label="Book">
                  {b.title}
                  {b.author === null ? null : <span className="ab-hint-quiet"> · {b.author}</span>}
                </td>
                <td data-label="Owner">{b.owner.handle ?? b.owner.email ?? "—"}</td>
                <td data-label="State">
                  <span className="ab-chip">{b.visibility}</span>
                  {b.takenDownAt !== null ? (
                    <>
                      {" "}
                      <span className="ab-chip ab-chip-bad">taken down</span>
                      <span className="ab-hint-quiet"> {b.takedownReason}</span>
                    </>
                  ) : null}
                  {b.publicRequestedAt !== null && b.visibility !== "public" ? (
                    <>
                      {" "}
                      <span className="ab-chip ab-chip-warn">asked {timeAgo(b.publicRequestedAt)}</span>
                    </>
                  ) : null}
                </td>
                <td data-label="">
                  <span className="ab-actions">
                    {b.publicRequestedAt !== null && b.visibility !== "public" && b.takenDownAt === null ? (
                      <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" disabled={busy === b.id} onClick={() => void act(b.id, "approve")}>
                        Make it public
                      </button>
                    ) : null}
                    {b.takenDownAt === null ? (
                      <button type="button" className="ab-btn ab-btn-danger ab-btn-sm" disabled={busy === b.id} onClick={() => void act(b.id, "takedown")}>
                        Take down
                      </button>
                    ) : (
                      <button type="button" className="ab-btn ab-btn-sm" disabled={busy === b.id} onClick={() => void act(b.id, "lift")}>
                        Put it back
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ── Rooms ────────────────────────────────────────────────────────────────── */

function RoomsTab(): ReactElement {
  const got = useAsk<{ rooms: { code: string; name: string; bookId: string; members: number; lastActivityAt: number }[]; note: string | null }>(
    "/admin/rooms",
    0,
  );

  if (got.error !== null) return <Panel bad>{got.error}</Panel>;
  const rooms = got.data?.rooms ?? [];

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Rooms</h2>
      {rooms.length === 0 ? (
        // ⚠️ Read from the real table, and this copy is rendered from the real answer being empty —
        // not from a phase constant. The day co-read ships, this screen starts working on its own.
        // A hardcoded "coming in P5" is the badge AGENTS.md caught bolted shut over a shipped feature.
        <p className="ab-hint">
          {got.data?.note ?? "No rooms."} Reading together is P5; this table is live and will fill itself
          the day a room exists.
        </p>
      ) : (
        <table className="ab-table">
          <thead>
            <tr>
              <th scope="col">Room</th>
              <th scope="col">Members</th>
              <th scope="col">Last active</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((r) => (
              <tr key={r.code}>
                <td data-label="Room">
                  {r.name} <code className="ab-sid">{r.code}</code>
                </td>
                <td data-label="Members" className="ab-num">
                  {r.members}
                </td>
                <td data-label="Last active">{timeAgo(r.lastActivityAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ── Health ───────────────────────────────────────────────────────────────── */

function HealthTab(): ReactElement {
  const [nonce, setNonce] = useState(0);
  const admin = useAsk<AdminHealth>("/admin/health", nonce);
  const status = useAsk<StatusReport>("/status", nonce);
  const [rolling, setRolling] = useState(false);
  const [rolled, setRolled] = useState<string | null>(null);

  const rollup = useCallback(async () => {
    setRolling(true);
    try {
      const r = await api.post<{ groups: number; rawDeleted: number; regenPruned: number }>("/admin/telemetry/rollup", {});
      setRolled(`${r.groups} group${r.groups === 1 ? "" : "s"} summarised · ${r.rawDeleted} raw rows deleted · ${r.regenPruned} regen rows pruned`);
      setNonce((n) => n + 1);
    } catch (err) {
      setRolled(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRolling(false);
    }
  }, []);

  const queue = useMemo(() => (status.data?.queue ?? []).filter((q) => q.status === "queued" || q.status === "running"), [status.data]);

  if (admin.error !== null) return <Panel bad>{admin.error}</Panel>;
  const a = admin.data;

  return (
    <>
      <section className="ab-panel">
        <h2 className="ab-h2">The box</h2>
        {/* From /api/v1/status — the dependency report (LAW 21). Not recomputed here; see the header. */}
        <dl className="ab-facts">
          <div>
            <dt>Disk</dt>
            <dd>{status.data?.disk?.percent === undefined ? "—" : `${status.data.disk.percent}%`}</dd>
          </div>
          <div>
            <dt>Origin</dt>
            <dd>
              {status.data?.origin?.reachable === true
                ? `reachable · ${status.data.origin.latencyMs ?? "?"} ms`
                : status.data?.origin === undefined
                  ? "—"
                  : "unreachable"}
            </dd>
          </div>
          <div>
            <dt>Queue</dt>
            <dd>{queue.length === 0 ? "empty" : queue.map((q) => `${q.lane} ${q.status} ${q.n}`).join(" · ")}</dd>
          </div>
          <div>
            <dt>Relayout p95</dt>
            <dd>
              {status.data?.layout?.p95Ms == null ? "not measured" : `${status.data.layout.p95Ms} ms`}
              {status.data?.layout?.alarm === true ? " ⚠︎" : ""}
            </dd>
          </div>
          <div>
            <dt>TTS</dt>
            <dd>{status.data?.tts?.mode ?? "—"}</dd>
          </div>
        </dl>
        {(status.data?.warnings ?? []).length > 0 ? (
          <ul className="ab-warnings">
            {(status.data?.warnings ?? []).map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="ab-panel">
        <h2 className="ab-h2">The library</h2>
        <dl className="ab-facts">
          <div>
            <dt>Books</dt>
            <dd>{a?.library.books ?? "—"}</dd>
          </div>
          <div>
            <dt>Editions</dt>
            <dd>{a?.library.editions ?? "—"}</dd>
          </div>
          <div>
            <dt>People</dt>
            <dd>{a?.library.users ?? "—"}</dd>
          </div>
          <div>
            <dt>Waiting for approval</dt>
            <dd>{a?.library.pendingPublic ?? "—"}</dd>
          </div>
          <div>
            <dt>Taken down</dt>
            <dd>{a?.library.takenDown ?? "—"}</dd>
          </div>
          <div>
            <dt>Clips</dt>
            <dd>{a?.audio.clips ?? "—"}</dd>
          </div>
          <div>
            <dt>Page artifacts</dt>
            <dd>{a?.audio.pageArtifacts ?? "—"}</dd>
          </div>
          <div>
            <dt>Lexicon</dt>
            <dd>{a?.lexicon.entries ?? "—"}</dd>
          </div>
        </dl>
      </section>

      <section className="ab-panel">
        <h2 className="ab-h2">Telemetry</h2>
        <dl className="ab-facts">
          <div>
            <dt>Regenerations</dt>
            <dd>
              {a?.telemetry.regenApplied ?? "—"} applied of {a?.telemetry.regenEvents ?? "—"}
            </dd>
          </div>
          <div>
            <dt>Unsettled</dt>
            <dd>{a?.telemetry.unsettled ?? "—"}</dd>
          </div>
          <div>
            <dt>Listen events</dt>
            <dd>
              {a?.telemetry.listenRaw ?? "—"} raw · {a?.telemetry.listenDaily ?? "—"} rolled up
            </dd>
          </div>
          <div>
            <dt>Oldest raw row</dt>
            <dd>{a?.telemetry.oldestRawAt == null ? "—" : timeAgo(a.telemetry.oldestRawAt)}</dd>
          </div>
          <div>
            <dt>Write buffer</dt>
            <dd>
              {a?.telemetry.buffer.pending ?? "—"} pending
              {(a?.telemetry.buffer.dropped ?? 0) > 0 ? ` · ${a?.telemetry.buffer.dropped} DROPPED` : ""}
            </dd>
          </div>
        </dl>
        {(a?.telemetry.unsettled ?? 0) > 0 ? (
          // The one number here that reports a WIRING failure rather than a load: an unsettled row
          // means the worker is not calling POST /internal/sentences/settle after a render. The audio
          // is perfect either way, which is exactly why it needs saying out loud.
          <p className="ab-hint-bad">
            {a?.telemetry.unsettled} regeneration{a?.telemetry.unsettled === 1 ? "" : "s"} never got their
            clip back from the worker. That is the settle call, not the audio — check that
            <code> audiobook-worker</code> can reach <code>/internal/sentences/settle</code>.
          </p>
        ) : null}
        <p className="ab-actions">
          <button type="button" className="ab-btn ab-btn-sm" disabled={rolling} onClick={() => void rollup()}>
            {rolling ? "Rolling up…" : "Roll up and prune now"}
          </button>
          {rolled === null ? null : <span className="ab-hint">{rolled}</span>}
        </p>
        <p className="ab-hint-quiet">
          Runs by itself every hour. Raw rows are kept 30 days and then summarised into daily buckets;
          regeneration rows are capped at 200 000, oldest reports first — the applied ones carry the
          text this whole screen is built from.
        </p>
      </section>
    </>
  );
}

/* ── shared bits ──────────────────────────────────────────────────────────── */

function Panel({ children, bad }: { children: ReactNode; bad?: boolean }): ReactElement {
  return <section className={bad ? "ab-panel ab-panel-bad" : "ab-panel"}>{children}</section>;
}

export default Admin;
